/**
 * Demo-side mirror of the pay math in migration 02.
 *
 * This exists so the bundled dataset computes money exactly the way
 * public.calculate_event_pay() does, instead of carrying hand-invented amounts
 * that would disagree with the real backend the moment it is connected. It is
 * used both when seeding and when the demo store recomputes an earning after an
 * assignment changes, which is what the trg_assignments_* triggers do in SQL.
 *
 * PLACEHOLDER MATH, exactly like the SQL it mirrors. The formula
 * (base + hourly * hours + travel) and every rate in the grid are our
 * invention, pending Michel's Drive workbook and Pascal's sign-off. Nothing
 * computed here is ever shown to a coach: earnings.is_visible_to_coach stays
 * false.
 */

import type {
  EarningBreakdown,
  LocationZone,
  PayRate,
  SeniorityLevel,
} from '../../types/db'

export interface PayInput {
  serviceId: string
  seniority: SeniorityLevel
  locationZone: LocationZone
  durationMinutes: number
  /** The event's local date, YYYY-MM-DD. Mirrors calculate_event_pay's p_on_date. */
  onDate: string
}

function round(value: number, places: number): number {
  const factor = 10 ** places
  return Math.round((value + Number.EPSILON) * factor) / factor
}

/**
 * Mirrors public.find_pay_rate(), including its tie-break:
 * `order by effective_from desc, min_duration_minutes desc limit 1`, so the
 * newest effective row wins and, among those, the band with the highest lower
 * bound - the most specific band the duration falls into, not the widest.
 */
export function findPayRate(
  rates: PayRate[],
  input: PayInput & { locationZone: LocationZone },
): PayRate | null {
  const matches = rates.filter(
    (r) =>
      r.service_id === input.serviceId &&
      r.seniority === input.seniority &&
      r.location_zone === input.locationZone &&
      input.durationMinutes >= r.min_duration_minutes &&
      input.durationMinutes <= r.max_duration_minutes &&
      r.effective_from <= input.onDate &&
      (r.effective_to === null || r.effective_to >= input.onDate),
  )
  if (matches.length === 0) return null
  matches.sort((a, b) => {
    const byDate = b.effective_from.localeCompare(a.effective_from)
    if (byDate !== 0) return byDate
    return b.min_duration_minutes - a.min_duration_minutes
  })
  return matches[0]
}

/**
 * Mirrors public.calculate_event_pay(), including both of its branches: the
 * out-of-zone fallback to dubai, and the sparse "no matching pay rate" object
 * that omits the four rate-derived fields entirely.
 */
export function calculateEventPay(rates: PayRate[], input: PayInput): EarningBreakdown {
  let rate = findPayRate(rates, input)
  let zone = input.locationZone
  let note: string | null = null

  if (!rate && input.locationZone !== 'dubai') {
    rate = findPayRate(rates, { ...input, locationZone: 'dubai' })
    if (rate) {
      zone = 'dubai'
      note = 'no rate for requested zone, fell back to dubai'
    }
  }

  if (!rate) {
    return {
      amount_aed: 0,
      currency: 'AED',
      pay_rate_id: null,
      is_placeholder: true,
      note: 'no matching pay rate',
      duration_minutes: input.durationMinutes,
      seniority: input.seniority,
      location_zone: input.locationZone,
      formula: 'base + hourly * hours + travel',
    }
  }

  const hours = round(input.durationMinutes / 60, 4)
  const amount = round(
    rate.base_amount_aed + rate.hourly_rate_aed * hours + rate.travel_allowance_aed,
    2,
  )

  return {
    amount_aed: amount,
    currency: 'AED',
    pay_rate_id: rate.id,
    is_placeholder: rate.is_placeholder,
    base_amount_aed: rate.base_amount_aed,
    hourly_rate_aed: rate.hourly_rate_aed,
    hours,
    travel_allowance_aed: rate.travel_allowance_aed,
    duration_minutes: input.durationMinutes,
    seniority: input.seniority,
    location_zone: zone,
    formula: 'base + hourly * hours + travel',
    note,
  }
}
